import { useEffect, useState } from "react";
import { Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, inr } from "../../lib/api";
import { GlassCard, Loader, Table } from "../kit";

const COUNT = { monthly: 12, quarterly: 4, annual: 1, "one-time": 1 };

export function FeeGenerate({ year }) {
  const { data } = useApi(`/fees/plans?academic_year=${encodeURIComponent(year)}`);
  const { data: tdata } = useApi("/fees/types");
  const [grade, setGrade] = useState("");
  const [pick, setPick] = useState([]);
  const [busy, setBusy] = useState(false);
  const tmap = Object.fromEntries((tdata?.types || []).map((t) => [t.id, t]));
  const plan = data?.grades.find((g) => g.grade === grade);
  useEffect(() => { setPick((plan?.items || []).filter((i) => tmap[i.fee_type_id]?.mandatory).map((i) => i.fee_type_id)); }, [grade, data, tdata]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data || !tdata) return <Loader />;
  const run = async () => {
    if (!window.confirm(`Create session ${year} fees for every student in ${grade}? Fees already created are skipped, never duplicated.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/fees/generate", { academic_year: year, grade, fee_type_ids: pick });
      toast.success(`${r.data.created} fee items created for ${r.data.students} students · ${r.data.skipped} already existed`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <GlassCard className="p-4 sm:p-6" data-testid="fee-generate">
      <p className="mb-4 text-sm text-zinc-400">Creates the whole session's fees (all monthly / quarterly instalments with due dates) for every student in the class, from its fee structure. Running it again only adds what's missing.</p>
      <select className="field sm:!w-64" value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="generate-grade-select">
        <option value="">Choose a class</option>{data.grades.map((g) => <option key={g.grade} value={g.grade}>{g.grade}{g.items.length ? "" : " — no structure"}</option>)}
      </select>
      {plan && (!plan.items.length ? <p className="mt-4 text-sm text-amber-300" data-testid="generate-no-plan">{grade} has no fee structure for {year}. Set it in Fee settings first.</p> : (
        <>
          <div className="mt-5"><Table testid="generate-items">
            <thead><tr><th></th><th>Fee</th><th>Frequency</th><th>Amount</th><th>Per student / session</th></tr></thead>
            <tbody>{plan.items.map((i) => { const t = tmap[i.fee_type_id]; return (
              <tr key={i.fee_type_id}><td><input type="checkbox" checked={pick.includes(i.fee_type_id)} onChange={(e) => setPick(e.target.checked ? [...pick, i.fee_type_id] : pick.filter((x) => x !== i.fee_type_id))} data-testid={`generate-pick-${i.fee_type_id}`} /></td>
                <td className="text-white">{t?.name}{t && !t.mandatory && <span className="ml-2 text-xs text-zinc-500">optional</span>}</td><td className="capitalize">{t?.frequency}</td><td>{inr(i.amount)}</td><td>{inr(i.amount * (COUNT[t?.frequency] || 1))}</td></tr>); })}</tbody>
          </Table></div>
          <button className="btn-primary mt-5" disabled={busy || !pick.length} onClick={run} data-testid="generate-run"><Lightning size={16} weight="fill" /> {busy ? "Creating…" : `Generate for all of ${grade}`}</button>
        </>
      ))}
    </GlassCard>
  );
}

function ReportTable({ title, rows, first, testid }) {
  return (
    <GlassCard className="p-4 sm:p-6">
      <h3 className="mb-4 font-display text-lg text-white">{title}</h3>
      {!rows.length ? <p className="text-sm text-zinc-500">No data.</p> : (
        <Table testid={testid}>
          <thead><tr><th>{first[0]}</th><th>Billed</th><th>Collected</th><th>Outstanding</th><th>Overdue</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r[first[1]]}><td className="text-white">{r[first[1]]}</td><td>{inr(r.billed)}</td><td className="text-emerald-300">{inr(r.collected)}</td><td className="text-orange-300">{inr(r.outstanding)}</td><td className="text-rose-300">{inr(r.overdue)}</td></tr>
          ))}</tbody>
        </Table>
      )}
    </GlassCard>
  );
}

export function FeeReports({ year, years, classes }) {
  const [f, setF] = useState({ academic_year: year, class_id: "" });
  const { data, loading } = useApi(`/fees/reports?academic_year=${encodeURIComponent(f.academic_year)}&class_id=${f.class_id}`);
  return (
    <div className="space-y-6" data-testid="fee-reports">
      <div className="flex flex-col gap-3 sm:flex-row">
        <select className="field sm:!w-44" value={f.academic_year} onChange={(e) => setF({ ...f, academic_year: e.target.value })} data-testid="report-year-select"><option value="">All sessions</option>{years.map((y) => <option key={y}>{y}</option>)}</select>
        <select className="field sm:!w-52" value={f.class_id} onChange={(e) => setF({ ...f, class_id: e.target.value })} data-testid="report-class-select"><option value="">All classes</option>{classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}</select>
        {data && <div className="text-sm text-zinc-400 sm:ml-auto sm:self-center">Collected <span className="text-emerald-300" data-testid="report-total-collected">{inr(data.total.collected)}</span> of {inr(data.total.billed)} · outstanding <span className="text-orange-300" data-testid="report-total-outstanding">{inr(data.total.outstanding)}</span></div>}
      </div>
      {loading || !data ? <Loader /> : (
        <>
          <ReportTable title="Fee-wise collection" rows={data.fee_wise} first={["Fee type", "fee_type"]} testid="report-fee-wise" />
          <ReportTable title="Class-wise collection" rows={data.class_wise} first={["Class", "class_name"]} testid="report-class-wise" />
          <GlassCard className="p-4 sm:p-6">
            <h3 className="mb-4 font-display text-lg text-white">Student-wise outstanding</h3>
            {!data.outstanding.length ? <p className="text-sm text-zinc-500">Nothing outstanding.</p> : (
              <Table testid="report-outstanding">
                <thead><tr><th>Student</th><th>Class</th><th>Items</th><th>Outstanding</th><th>Overdue</th></tr></thead>
                <tbody>{data.outstanding.slice(0, 300).map((s) => (
                  <tr key={s.student_id}><td className="text-white">{s.name}</td><td>{s.class_name}</td><td>{s.items}</td><td className="text-orange-300">{inr(s.outstanding)}</td><td className="text-rose-300">{inr(s.overdue)}</td></tr>
                ))}</tbody>
              </Table>
            )}
          </GlassCard>
        </>
      )}
    </div>
  );
}
